import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import { AccoListerAcco } from '@/scopes/common/domain/acco-lister/types'
import { formatParticipantDistributionToGA4Result } from '@/scopes/search/search/domain/analytics/mappers'
import { formatLocations } from '@/scopes/search/search/infrastructure/api/searchResponseMapper'
import {
  Campaign,
  Collection,
  SearchResult,
  SearchResultPrice,
} from '@/scopes/search/search/infrastructure/api/types'

export interface SearchFilterClickEventProps {
  element: 'checkbox' | 'radio button' | 'slider' | 'calendar' | 'participants selector' | 'textField'
  type: string
  result: string | number | readonly string[] | undefined
}

export function createSearchFilterClickEvent({
  element,
  type,
  result,
}: SearchFilterClickEventProps): GA4AnalyticsEvent {
  return {
    event: 'clickFilter',
    description: 'set filter on product listing',
    element,
    type,
    result: result?.toString().toLowerCase(),
    interaction: true,
  }
}

export function createSearchProductClickEvent(result: SearchResult): GA4AnalyticsEvent {
  const occupancy
    = (result.occupancyInfo?.adultsCount ?? 0)
      + (result.occupancyInfo?.childrenCount ?? 0)
      + (result.occupancyInfo?.babiesCount ?? 0)
  return {
    event: 'eec.impressionClick',
    ecommerce: {
      click: {
        actionField: {
          list: 'search result',
        },
        products: [
          {
            id: result.id,
            name: result.name,
            category: formatLocations(result.locations),
            badges: result.collection,
            accoStars: result.rating,
            rating: result.userRating,
            entryPrice: result.price?.averagePrice,
            campaignId: result.campaign?.id,
            campaignName: result.campaign?.title,
            countryId: result.locations[0].deskId,
            regionId: result.locations[1].deskId,
            cityId: result.locations[2].deskId,
            country: result.locations[0].englishName,
            region: result.locations[1].englishName,
            city: result.locations[2].englishName,
            list: 'search result',
            duration: result.featuredFilters?.duration,
            totalGuests: occupancy,
            departureDate: result.departureDateDetail,
          },
        ],
      },
    },
  }
}

export interface FavoriteClickedEventProps {
  campaign?: Campaign
  collection?: Collection | string
  departureDateDetail?: string
  countryName: string
  regionName: string
  cityName: string
  cityId: string | number
  countryId: string | number
  regionId: string | number
  countryEnglishName: string
  regionEnglishName: string
  cityEnglishName: string
  featuredFilters?: {
    duration: string
    occupancy: string
    mealplan: string
  }
  id: number
  name?: string
  occupancyInfo?: {
    adultsCount: number
    childrenCount: number
    babiesCount: number
  }
  price?: SearchResultPrice
  rating?: number
  userRating: number
  origin: string
}

interface CreateFavoriteClickedEventActionResult extends FavoriteClickedEventProps {
  actionResult: 'marked_as_favorite' | 'not_marked_as_favorite'
}

export function createFavoriteClickedEvent({
  occupancyInfo,
  id,
  name,
  cityId,
  countryId,
  regionId,
  countryName,
  regionName,
  cityName,
  countryEnglishName,
  regionEnglishName,
  cityEnglishName,
  collection,
  rating,
  userRating,
  price,
  campaign,
  featuredFilters,
  departureDateDetail,
  origin,
  actionResult,
}: CreateFavoriteClickedEventActionResult): GA4AnalyticsEvent {
  const occupancy
    = (occupancyInfo?.adultsCount ?? 0)
      + (occupancyInfo?.childrenCount ?? 0)
      + (occupancyInfo?.babiesCount ?? 0)
  return {
    event: 'eec.impressionClick',
    ecommerce: {
      click: {
        actionField: {
          list: origin,
          result: actionResult,
        },
        products: [
          {
            id,
            name,
            category: `${countryName} > ${regionName} > ${cityName}`,
            badges: collection ?? '',
            accoStars: rating,
            rating: userRating,
            ...(price && { entryPrice: price?.averagePrice }),
            ...(campaign && { campaignId: campaign?.id }),
            ...(campaign && { campaignName: campaign?.title }),
            countryId,
            regionId,
            cityId,
            country: countryEnglishName,
            region: regionEnglishName,
            city: cityEnglishName,
            list: origin,
            ...(featuredFilters?.duration && { duration: featuredFilters?.duration }),
            ...(occupancy && { totalGuests: occupancy }),
            ...(departureDateDetail && { departureDate: departureDateDetail }),
          },
        ],
      },
    },
  }
}

export function createSearchRecommendationClickEvent(acco: AccoListerAcco, isLoggedIn: boolean, brand: string) {
  const { distribution: partyComposition, total: totalGuests } = formatParticipantDistributionToGA4Result(
    acco.price?.participants || [],
  )
  return {
    event: 'select_item',
    ecommerce: {
      item_list_id: 'reco_list_pdp_01',
      item_list_name: 'recomended accomodations on pdp',
      items: [
        {
          item_id: acco?.id,
          item_name: acco?.name,
          item_brand: brand,
          item_category: acco?.country,
          item_category2: acco?.region,
          item_category3: acco?.city,
          item_list_id: 'reco_list_pdp_01',
          item_list_name: 'recomended accomodations on pdp',
          price: acco?.price?.averagePrice,
        },
      ],
    },
    logged_in: isLoggedIn,
    accoId: acco?.id,
    transport: acco?.price?.transportType,
    boardType: acco?.price?.mealplan?.name,
    duration: acco?.price.durationInDays,
    partyComposition,
    totalGuests,
    departureDate: acco?.price?.departureDate,
    entryPrice: acco?.price?.averagePrice,
    campaignId: acco?.campaign?.id,
    promotedPrice: 'shown',
    cityId: acco?.locations?.[2]?.deskId,
    regionId: acco?.locations?.[1]?.deskId,
    countryId: acco?.locations?.[0]?.deskId,
    city: acco?.locations?.[2]?.englishName,
    region: acco?.locations?.[1]?.englishName,
    country: acco?.locations?.[0]?.englishName,
  }
}

export function createSearchRecommendationInViewportEvent(accos: AccoListerAcco[]) {
  return {
    event: 'eec.impressionView_recommendation',
    type: 'flag',
    interaction: false,
    ecommerce: {
      impressions: accos.map((acco: AccoListerAcco, i: number) => ({
        list: 'recomended accomodations on pdp',
        id: acco?.id,
        name: acco?.name,
        duration: `${acco?.price?.durationInDays} dagen`,
        totalGuests: formatParticipantDistributionToGA4Result(acco.price?.participants || [])?.total,
        departureDate: acco?.price?.departureDate,
        category: formatLocations(acco?.locations ?? []),
        position: i + 1,
        accoStars: acco?.rating,
        badges: acco?.collection,
        rating: acco?.userRating,
        entryPrice: acco?.price?.averagePrice,
        campaignId: acco?.campaign?.id,
        campaignName: acco?.campaign?.title,
        cityId: acco?.locations?.[2]?.deskId,
        regionId: acco?.locations?.[1]?.deskId,
        countryId: acco?.locations?.[0]?.deskId,
      })),
    },
  }
}
